import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useToast } from '../components/Toast';
import { Avatar, EmptyState, PageSkeleton } from '../components/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/format';
import { keys, useInvalidateGroup } from '../lib/queries';
import { haptic } from '../lib/telegram';
import { Icon } from '../components/Icon';

export function JoinPage() {
  const { code = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateGroup();
  const [claim, setClaim] = useState<string | undefined>(undefined);
  const [joining, setJoining] = useState(false);
  const preview = useQuery({ queryKey: keys.invite(code), queryFn: () => api.previewInvite(code), retry: false });

  if (preview.isPending) return <PageSkeleton />;
  if (preview.error || !preview.data) {
    return (
      <main className="page page--no-tabbar app">
        <EmptyState
          icon="link"
          title={t('join.invalid')}
          action={
            <Link className="btn btn--soft" to="/">
              {t('nav.home')}
            </Link>
          }
        />
      </main>
    );
  }

  const { group, memberCount, guests, alreadyMember } = preview.data;

  const join = async () => {
    setJoining(true);
    try {
      await api.joinGroup(code, claim);
      await invalidate(group.id);
      haptic.success();
      void navigate(`/groups/${group.id}`, { replace: true });
    } catch (error) {
      toast(errorMessage(error, t), 'error');
      setJoining(false);
    }
  };

  return (
    <main className="page page--no-tabbar app">
      <div className="hero" style={{ textAlign: 'center' }}>
        <div className="hero-label">{t('join.title')}</div>
        <div className="hero-amount" style={{ fontSize: 26 }}>
          {group.name}
        </div>
        {group.description && <div className="hero-label">{group.description}</div>}
        <div className="hero-sub" style={{ justifyContent: 'center' }}>
          <span className="hero-pill hero-pill--icon">
            <Icon name="members" size={14} />
            {t('common.members', { count: memberCount })}
          </span>
          <span className="hero-pill">{group.currency}</span>
        </div>
      </div>

      {alreadyMember ? (
        <div className="card stack" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0 }}>{t('join.alreadyMember')}</p>
          <Link className="btn btn--primary" to={`/groups/${group.id}`} replace>
            {t('join.open')}
          </Link>
        </div>
      ) : (
        <>
          {guests.length > 0 && (
            <div className="card card--flush">
              <div style={{ padding: '14px 16px 4px' }}>
                <div className="list-title">{t('join.whoAreYou')}</div>
                <div className="muted small">{t('join.whoAreYouHint')}</div>
              </div>
              <div className="list">
                {[{ id: undefined, displayName: t('join.newMember') }, ...guests].map((g) => (
                  <button
                    key={g.id ?? 'new'}
                    type="button"
                    className="list-row"
                    aria-pressed={claim === g.id}
                    onClick={() => (haptic.select(), setClaim(g.id))}
                  >
                    <span className={`checkbox ${claim === g.id ? 'checkbox--on' : ''}`} style={{ borderRadius: '50%' }} aria-hidden>
                      {claim === g.id && <Icon name="check" size={14} />}
                    </span>
                    {g.id ? <Avatar name={g.displayName} small /> : <span className="avatar avatar--sm" style={{ background: 'var(--primary)', color: 'var(--on-primary)' }}>
                        <Icon name="plus" size={16} />
                      </span>}
                    <span className="list-title">{g.displayName}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <button className="btn btn--primary btn--block" disabled={joining} onClick={() => void join()}>
            {t('join.join')}
          </button>
        </>
      )}
    </main>
  );
}
